"use client"
import { useRouter } from "next/navigation"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { cn, formatPrice } from "@/lib/utils"
import { useCart } from "../providers/CartContext"
import Icons from "../ui/icons"
import { Badge } from "../ui/badge"
import { toast } from "sonner"
import { useState } from "react"
import { LoaderCircleIcon } from "lucide-react"

export default function CartSheetContent() {
    const { items, totalPrice, removeItem, clearCart } = useCart()
    const router = useRouter()
    const [isOrderProcessing, setIsOrderProcessing] = useState(false);
    const handleCheckout = async () => {
        setIsOrderProcessing(true)
        try {
            if (items.length === 0) {
                toast.warning("سبد خرید شما خالی است.")
                setIsOrderProcessing(false)
                return
            }

            const payload = {
                items: items.map((item) => ({
                    product: { _id: item.product._id },
                    deliveryMethod: item.deliveryMethod,
                    quantity: item.quantity,
                    priceAtPurchase: item.priceAtPurchase,
                    validpl: item.validpl,
                    userInput: item.userInput || "",
                })),
            }

            const res = await fetch("/api/order/create", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            })

            const data = await res.json()

            if (!res.ok) {
                toast.error(data?.error || "خطا در ثبت سفارش.")
                setIsOrderProcessing(false)
                return
            }

            setIsOrderProcessing(false)
            toast.success("سفارش با موفقیت ثبت شد.")
            clearCart()
            router.push(`/dashboard/checkout/${data.order_id}`)
        } catch (err) {
            setIsOrderProcessing(false)
            console.error("❌ Checkout error:", err)
            toast.error("خطایی هنگام ثبت سفارش رخ داد.")
        }
    }
    return (
        <div className={cn("flex flex-col  gap-4", items.length > 0 && "h-full")}>
            {items.length === 0 && (
                <div className="w-full h-screen flex items-center justify-center"><p className="text-muted-foreground">سبد شما خالی است.</p></div>
            )}

            <div className="flex h-full flex-col justify-between">
                <div className="space-y-2 mt-7">
                    {items.map((item: any) => (
                        <div key={item.product._id} className="flex items-center gap-4 border-b-[1.5px] border-b-[#1C1B1D] pb-2">
                            <Image src={item.product.coverImage} width={1000} height={1000} alt={item.product.title} className="rounded w-20 h-20" />
                            <div className="flex-1 space-y-2">
                                <p className="font-medium text-sm">{item.product.title}</p>
                                <Badge><p className="text-sm">{item.quantity} عدد</p></Badge>
                                <p className="text-sm">{formatPrice(item.priceAtPurchase)} تومان</p>
                            </div>
                            <Button size="icon" className="rounded-full" variant="subtle" onClick={() => removeItem(item.product._id)}>
                                <Icons.rubbish className="w-7 h-7" />
                            </Button>
                        </div>
                    ))}
                </div>
                <div className="space-y-2 mb-5">
                    {items.length > 0 && (
                        <>
                            <div className="flex justify-between font-bold mt-4">
                                <span>مبلغ نهایی:</span>
                                <span>{formatPrice(totalPrice)} تومان</span>
                            </div>
                            <Button
                                disabled={isOrderProcessing}
                                onClick={handleCheckout}
                                className="bg-purple-900/50 hover:bg-purple-900/40 transition mt-2 w-full"
                            >
                                {isOrderProcessing ? (
                                    <LoaderCircleIcon className="animate-spin !w-6 !h-6" aria-hidden="true" />
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <Icons.order className="!w-5 !h-5" />
                                        <p>ادامه خرید</p>
                                    </div>
                                )}
                            </Button>
                            <Button variant="ghost" className="w-full" onClick={clearCart}>
                                پاک کردن سبد
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
